//src/components/Materials2050Explorer.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Separator } from "./ui/separator";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from "./ui/dropdown-menu";
import { Loader2, Search, ChevronLeft, ChevronRight, ArrowRightLeft } from "lucide-react";

import {
  fetchProductFilters,
  searchProductsOpen,
  searchProductsFull,
  loadProductDetailsOpen,
  loadProductDetailsFull,
  normalize2050ListItem,
  normalize2050Detail
} from "../services/materials2050Service";

const UI_PAGE_SIZE = 100;
const SERVER_PREFETCH_PAGES_AHEAD = 1;
const SERVER_PREFETCH_MAX_PAGES = 3;
const MIN_QUERY_LEN = 2;
const SAMPLE_QUERIES = ["concrete", "timber", "insulation", "steel", "gypsum"];

function formatImpactValue(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  const absValue = Math.abs(num);
  if (absValue >= 1000) return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (absValue >= 1) return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (absValue === 0) return "0";
  if (absValue < 0.001) return num.toExponential(2);
  return num.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function normalizeUnitLabel(raw) {
  if (!raw) return raw;
  let s = String(raw);
  s = s.replace(/�/g, '');
  s = s.replace(/m�/g, 'm3');
  s = s.replace(/m�/g, 'm2');
  s = s.replace(/CO2-�qv\.?/gi, 'CO2-eq');
  s = s.replace(/CO2-?eqv\.?/gi, 'CO2-eq');
  s = s.replace(/CO2\s*eq\.?/gi, 'CO2-eq');
  return s;
}


function asArray(value) {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function formatLoadStamp() {
  const now = new Date();
  const formatted = now.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  return `Loaded from 2050-materials on ${formatted}`;
}

function formatSelectionSummary(values, { fallback = "All" } = {}) {
  if (!Array.isArray(values) || values.length === 0) return fallback;
  if (values.length <= 2) return values.join(", ");
  return `${values.slice(0, 2).join(", ")} +${values.length - 2}`;
}

function collectFacetValues(rawValue) {
  const out = [];
  const arr = Array.isArray(rawValue) ? rawValue : rawValue != null ? [rawValue] : [];

  for (const v of arr) {
    if (v == null) continue;

    if (typeof v === "object") {
      const candidates = [v.id, v.pk, v.code, v.key, v.uuid, v.slug, v.value, v.name, v.label, v.title, v.text];
      for (const c of candidates) {
        if (c == null || c === "") continue;
        out.push(String(c));
      }
      continue;
    }

    out.push(String(v));
  }

  return out;
}

function filterOptionsByAvailable(options, availableSet, selectedIds) {
  const selectedSet = new Set((selectedIds || []).map(String));

  if (!availableSet || availableSet.size === 0) {
    return options;
  }

  return options.filter((o) => {
    const id = String(o.id);
    const name = String(o.name);
    return selectedSet.has(id) || availableSet.has(id) || availableSet.has(name);
  });
}

function normalizeFilterItems(rawList) {
  if (rawList == null) return [];

  // 2050 filter format: { filter_name, filter_type, filter_options: [ { id, value }, ... ] }
  let list = [];
  if (rawList && typeof rawList === "object" && Array.isArray(rawList.filter_options)) {
    list = rawList.filter_options;
  } else if (Array.isArray(rawList)) {
    list = rawList;
  } else if (rawList && typeof rawList === "object") {
    const maybeArray = rawList.options || rawList.values || rawList.items;
    if (Array.isArray(maybeArray)) list = maybeArray;
  }

  return list
    .map((it) => {
      if (it == null) return null;

      if (typeof it === "object") {
        const id = it.id ?? it.pk ?? it.code ?? it.key ?? it.uuid ?? it.slug ?? it.value ?? it.name;
        const name = it.value ?? it.name ?? it.label ?? it.title ?? it.text ?? id;
        if (id == null || name == null) return null;
        return { id: String(id), name: String(name) };
      }

      return { id: String(it), name: String(it) };
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
}

const ImpactCategoryTable = ({ impact, declaredUnitLabel }) => {
  const modules = impact?.modules || {};
  const rows = Object.entries(modules);

  const ordered = ["A1-A3", "A4", "A5", "B1", "B2", "B3", "B4", "B5", "B6", "B7", "C1", "C2", "C3", "C4", "D"];
  rows.sort((a, b) => ordered.indexOf(a[0]) - ordered.indexOf(b[0]));

  return (
    <Card className="shadow-sm border-emerald-100/80 bg-white">
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base text-emerald-700 flex items-center gap-2">
            <span>{impact?.description || "Impact"}</span>
            {impact?.unit && (
              <Badge variant="secondary" className="text-xs bg-emerald-50 text-emerald-700">
                {normalizeUnitLabel(impact.unit)} / {normalizeUnitLabel(declaredUnitLabel) || "declared unit"}
              </Badge>
            )}
          </CardTitle>
          <Badge className="bg-emerald-600 text-white text-xs">
            {formatImpactValue(impact?.total)} {impact?.unit || ""}
          </Badge>
        </div>
        <CardDescription className="text-xs">Impacts per {normalizeUnitLabel(declaredUnitLabel) || "declared unit"}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-hidden rounded-lg border border-emerald-100/70">
          <table className="w-full text-sm">
            <thead className="bg-emerald-50 text-emerald-700">
              <tr>
                <th className="text-left font-medium px-3 py-2">Module</th>
                <th className="text-right font-medium px-3 py-2">Impact / {normalizeUnitLabel(declaredUnitLabel) || "declared unit"}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k} className="odd:bg-white even:bg-emerald-50/40">
                  <td className="px-3 py-2 font-medium text-emerald-800">{k}</td>
                  <td className="px-3 py-2 text-right text-emerald-900">{formatImpactValue(v)}</td>
                </tr>
              ))}
              <tr className="bg-emerald-100/60 text-emerald-900 font-semibold">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{formatImpactValue(impact?.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const ProductResultItem = ({ item, isSelected, onSelect }) => {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className={`w-full text-left p-4 rounded-xl border transition shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500/60 flex flex-col gap-1 ${
        isSelected ? "border-emerald-500 bg-emerald-50/70" : "border-emerald-100 bg-white/80"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="font-semibold text-gray-800 line-clamp-2">{item.displayName}</span>
        <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-[11px]">
          {item.updated ? "Updated" : "Item"}
        </Badge>
      </div>
      <div className="text-xs text-gray-600 flex flex-wrap gap-2">
        {item.company && (
          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-white">
            {item.company}
          </Badge>
        )}
        {item.epd_operator && (
          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-white">
            {item.epd_operator}
          </Badge>
        )}
      </div>
    </button>
  );
};

const SkeletonProductItem = () => (
  <div className="w-full p-4 rounded-xl border border-emerald-100 bg-white/80 shadow-sm">
    <div className="animate-pulse space-y-3">
      <div className="h-4 bg-emerald-100/70 rounded w-3/4" />
      <div className="flex gap-2">
        <div className="h-5 bg-emerald-50 rounded w-20" />
        <div className="h-5 bg-emerald-50 rounded w-24" />
        <div className="h-5 bg-emerald-50 rounded w-16" />
      </div>
    </div>
  </div>
);

const Materials2050Explorer = ({ onShortlist, shortlist = [], onStatusChange, onRegisterRefresh }) => {
  const inputRef = useRef(null);

  const [connectionStatus, setConnectionStatus] = useState("loading");
  const [connectionError, setConnectionError] = useState(null);

  const [filtersRaw, setFiltersRaw] = useState(null);

  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [selectedMaterialTypes, setSelectedMaterialTypes] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedCertificateTypes, setSelectedCertificateTypes] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [committedQuery, setCommittedQuery] = useState("");
  const [committedFilters, setCommittedFilters] = useState({});
  const [searchNonce, setSearchNonce] = useState(0);

  const [searchStatus, setSearchStatus] = useState("idle");
  const [searchError, setSearchError] = useState(null);

  const [buffer, setBuffer] = useState([]);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [serverPageSize, setServerPageSize] = useState(200);
  const [totalCount, setTotalCount] = useState(0);
  const [highestServerPageLoaded, setHighestServerPageLoaded] = useState(0);
  const [lastServerError, setLastServerError] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);

  const [currentPage, setCurrentPage] = useState(0);

  const [selectedItem, setSelectedItem] = useState(null);
  const [detailStatus, setDetailStatus] = useState("idle");
  const [detailError, setDetailError] = useState(null);
  const [detail, setDetail] = useState(null);

  const [selectedImpactId, setSelectedImpactId] = useState(null);
  const mode = "open";

  const loadedServerPagesRef = useRef(new Set());
  const runIdRef = useRef(0);
  const prefetchingRef = useRef(false);
  const activeControllerRef = useRef(null);
  const seenItemKeysRef = useRef(new Set());
  const inFlightPagesRef = useRef(new Set());

  const getItemKey = useCallback((it) => {
    return String(
      it?.uuid || it?.raw?.unique_product_uuid_v2 || it?.raw?.product_url || it?.displayName || ""
    );
  }, []);

  const isAbortError = useCallback((error, controller) => {
    if (!error) return false;
    if (error.name === "AbortError") return true;
    if (controller?.signal?.aborted) return true;
    return false;
  }, []);


  const filtersData = useMemo(() => {
    if (filtersRaw?.filters) return filtersRaw.filters;
    if (filtersRaw?.data) return filtersRaw.data;
    return filtersRaw;
  }, [filtersRaw]);

  const productTypeOptions = useMemo(() => {
    return normalizeFilterItems(filtersData?.product_type);
  }, [filtersData]);

  const materialTypeOptions = useMemo(() => {
    return normalizeFilterItems(filtersData?.material_types || filtersData?.material_type);
  }, [filtersData]);

  const countryOptions = useMemo(() => {
    return normalizeFilterItems(filtersData?.manufacturing_country);
  }, [filtersData]);

  const certificateTypeOptions = useMemo(() => {
    return normalizeFilterItems(filtersData?.certificate_type);
  }, [filtersData]);

  const availableFacets = useMemo(() => {
    const sets = {
      product_type: new Set(),
      material_types: new Set(),
      manufacturing_country: new Set(),
      certificate_type: new Set()
    };

    const presence = {
      product_type: false,
      material_types: false,
      manufacturing_country: false,
      certificate_type: false
    };

    for (const it of buffer) {
      const raw = it?.raw || {};

      const add = (key, value) => {
        const vals = collectFacetValues(value);
        if (vals.length > 0) presence[key] = true;
        for (const s of vals) sets[key].add(s);
      };

      add("product_type", raw.product_type);
      add("material_types", raw.material_types ?? raw.material_type);
      add("manufacturing_country", raw.manufacturing_country ?? raw.manufacturing_location ?? raw.country ?? raw.city);
      add("certificate_type", raw.certificate_type);
    }

    return { sets, presence };
  }, [buffer]);

  const productTypeOptionsResponsive = useMemo(() => {
    return filterOptionsByAvailable(productTypeOptions, availableFacets.sets.product_type, selectedProductTypes);
  }, [productTypeOptions, availableFacets, selectedProductTypes]);

  const materialTypeOptionsResponsive = useMemo(() => {
    return filterOptionsByAvailable(materialTypeOptions, availableFacets.sets.material_types, selectedMaterialTypes);
  }, [materialTypeOptions, availableFacets, selectedMaterialTypes]);

  const countryOptionsResponsive = useMemo(() => {
    return filterOptionsByAvailable(countryOptions, availableFacets.sets.manufacturing_country, selectedCountries);
  }, [countryOptions, availableFacets, selectedCountries]);

  const certificateTypeOptionsResponsive = useMemo(() => {
    if (!availableFacets.presence.certificate_type) return [];
    return filterOptionsByAvailable(certificateTypeOptions, availableFacets.sets.certificate_type, selectedCertificateTypes);
  }, [certificateTypeOptions, availableFacets, selectedCertificateTypes]);

  const filterKeys = useMemo(() => {
    return {
      productType: "product_type",
      materialType: "material_types",
      country: "manufacturing_country",
      certificate: "certificate_type"
    };
  }, []);

  const productTypeSummary = useMemo(
    () =>
      formatSelectionSummary(
        selectedProductTypes.map((id) => productTypeOptionsResponsive.find((o) => o.id === id)?.name || id)
      ),
    [selectedProductTypes, productTypeOptionsResponsive]
  );
  const materialTypeSummary = useMemo(
    () =>
      formatSelectionSummary(
        selectedMaterialTypes.map((id) => materialTypeOptionsResponsive.find((o) => o.id === id)?.name || id)
      ),
    [selectedMaterialTypes, materialTypeOptionsResponsive]
  );
  const countrySummary = useMemo(
    () =>
      formatSelectionSummary(selectedCountries.map((id) => countryOptionsResponsive.find((o) => o.id === id)?.name || id)),
    [selectedCountries, countryOptionsResponsive]
  );
  const certificateSummary = useMemo(
    () =>
      formatSelectionSummary(
        selectedCertificateTypes.map((id) => certificateTypeOptionsResponsive.find((o) => o.id === id)?.name || id)
      ),
    [selectedCertificateTypes, certificateTypeOptionsResponsive]
  );

  const draftFilters = useMemo(() => {
    const out = {};

    const toName = (ids, options) =>
      ids.map((id) => options.find((o) => o.id === id)?.name || id);

    if (selectedProductTypes.length) out[filterKeys.productType] = selectedProductTypes;
    if (selectedMaterialTypes.length) out[filterKeys.materialType] = selectedMaterialTypes;

    if (selectedCountries.length) {
      out[filterKeys.country] = toName(selectedCountries, countryOptionsResponsive);
    }

    if (selectedCertificateTypes.length) out[filterKeys.certificate] = selectedCertificateTypes;

    return out;
  }, [
    selectedProductTypes,
    selectedMaterialTypes,
    selectedCountries,
    selectedCertificateTypes,
    filterKeys,
    countryOptionsResponsive
  ]);

  const committedFiltersKey = useMemo(() => JSON.stringify(committedFilters || {}), [committedFilters]);

  const totalPages = useMemo(() => {
    return totalCount > 0 ? Math.ceil(totalCount / UI_PAGE_SIZE) : 0;
  }, [totalCount]);

  const pageResults = useMemo(() => {
    const start = currentPage * UI_PAGE_SIZE;
    return buffer.slice(start, start + UI_PAGE_SIZE);
  }, [buffer, currentPage]);

  const canGoPrev = totalPages > 0 && currentPage > 0;
  const canGoNext = totalPages > 0 && currentPage < totalPages - 1;

  const loadFilters = useCallback(async () => {
    setConnectionStatus("loading");
    setConnectionError(null);
    try {
      const data = await fetchProductFilters({ mini: true });
      setFiltersRaw(data);
      setConnectionStatus("success");
    } catch (e) {
      setConnectionError(String(e?.message || e));
      setConnectionStatus("error");
    }
  }, []);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    if (typeof onRegisterRefresh === "function") onRegisterRefresh(loadFilters);
  }, [onRegisterRefresh, loadFilters]);

  useEffect(() => {
    if (typeof onStatusChange === "function") {
      onStatusChange({
        status: connectionStatus,
        error: lastServerError?.message || connectionError || null,
        meta: { datasetInfo: datasetInfo || formatLoadStamp() }
      });
    }
  }, [connectionStatus, connectionError, datasetInfo, lastServerError, onStatusChange]);


  const toggleValue = useCallback((value, selected, setSelected) => {
    setSelected((prev) => {
      const exists = prev.includes(value);
      if (!exists && selected) return [...prev, value];
      if (exists && !selected) return prev.filter((v) => v !== value);
      return prev;
    });
  }, []);

  const handleSelectSample = useCallback((sample) => {
    setSearchTerm(sample);
    setCurrentPage(0);
  }, []);

  const commitSearch = useCallback(() => {
    setCommittedQuery(searchTerm.trim());
    setCommittedFilters(draftFilters);
    setSearchNonce((n) => n + 1);
    setCurrentPage(0);
  }, [searchTerm, draftFilters]);

  useEffect(() => {
    const hasAnyCommittedFilters = committedFilters && Object.keys(committedFilters).length > 0;
    const q = committedQuery.trim();

    if (!q && !hasAnyCommittedFilters) {
      setSearchStatus("idle");
      setSearchError(null);
      setBuffer([]);
      setTotalCount(0);
      return;
    }

    if (q && q.length < MIN_QUERY_LEN && !hasAnyCommittedFilters) {
      setSearchStatus("idle");
      setSearchError(null);
      setBuffer([]);
      setTotalCount(0);
      return;
    }

    const runId = (runIdRef.current += 1);

    if (activeControllerRef.current) activeControllerRef.current.abort();
    const controller = new AbortController();
    activeControllerRef.current = controller;

    setSearchStatus("loading");
    setSearchError(null);

    setCurrentPage(0);
    setSelectedItem(null);
    setDetail(null);
    setDetailStatus("idle");
    setDetailError(null);

    setBuffer([]);
    setTotalCount(0);
    setHighestServerPageLoaded(0);
    loadedServerPagesRef.current = new Set();
    seenItemKeysRef.current = new Set();
    inFlightPagesRef.current = new Set();

    (async () => {
      try {
        const fn = mode === "full" ? searchProductsFull : searchProductsOpen;

        const r = await fn(q, {
          signal: controller.signal,
          page: 1,
          filters: committedFilters
        });

        if (runIdRef.current !== runId) return;

        const items = (r?.data || []).map(normalize2050ListItem);

        const uniqueItems = [];
        for (const it of items) {
          const key = getItemKey(it);
          if (!key) continue;
          if (seenItemKeysRef.current.has(key)) continue;
          seenItemKeysRef.current.add(key);
          uniqueItems.push(it);
        }

        loadedServerPagesRef.current.add(1);
        setBuffer(uniqueItems);
        setTotalCount(Number.isFinite(Number(r?.totalCount)) ? Number(r.totalCount) : items.length);
        setServerPageSize(Number.isFinite(Number(r?.pageSize)) ? Number(r.pageSize) : 200);
        setHighestServerPageLoaded(1);
        setLastServerError(null);
        setDatasetInfo(formatLoadStamp());

        setSearchStatus("success");
      } catch (e) {
        if (isAbortError(e, controller)) return;
        setLastServerError({
          status: e?.status ?? null,
          message: String(e?.message || e)
        });
        setDatasetInfo(null);
        setSearchStatus("error");
        setSearchError(String(e?.message || e));
      }
    })();

    return () => controller.abort();
  }, [committedQuery, committedFiltersKey, searchNonce, mode]);

  useEffect(() => {
    if (searchStatus !== "success") return;
    if (!totalCount) return;

    const controller = activeControllerRef.current;
    if (!controller) return;

    const startIndex = currentPage * UI_PAGE_SIZE;
    const requiredServerPage = Math.floor(startIndex / Math.max(1, serverPageSize)) + 1;
    const query = committedQuery.trim();

    const runId = runIdRef.current;

    (async () => {
      if (requiredServerPage <= highestServerPageLoaded) return;

      setIsFetchingMore(true);

      try {
        const fn = mode === "full" ? searchProductsFull : searchProductsOpen;

        for (let p = highestServerPageLoaded + 1; p <= requiredServerPage; p += 1) {
          if (runIdRef.current !== runId) return;
          if (controller.signal.aborted) return;
          if (loadedServerPagesRef.current.has(p)) continue;
          if (inFlightPagesRef.current.has(p)) continue;

          inFlightPagesRef.current.add(p);
          try {
            const r = await fn(query, {
              signal: controller.signal,
              page: p,
              filters: committedFilters
            });

            if (runIdRef.current !== runId) return;

            const items = (r?.data || []).map(normalize2050ListItem);

            const uniqueItems = [];
            for (const it of items) {
              const key = getItemKey(it);
              if (!key) continue;
              if (seenItemKeysRef.current.has(key)) continue;
              seenItemKeysRef.current.add(key);
              uniqueItems.push(it);
            }

            loadedServerPagesRef.current.add(p);
            setBuffer((prev) => prev.concat(uniqueItems));
            setTotalCount(Number.isFinite(Number(r?.totalCount)) ? Number(r.totalCount) : totalCount);
            setServerPageSize(Number.isFinite(Number(r?.pageSize)) ? Number(r.pageSize) : serverPageSize);
            setHighestServerPageLoaded((prev) => Math.max(prev, p));
            setDatasetInfo((prev) => prev || formatLoadStamp());
            await sleep(250);
          } catch (err) {
            if (isAbortError(err, controller)) return;
            console.error("Failed to fetch 2050 Materials page", err);
            setLastServerError({
              status: err?.status ?? null,
              message: String(err?.message || err)
            });
          } finally {
            inFlightPagesRef.current.delete(p);
          }
        }
      } finally {
        setIsFetchingMore(false);
      }
    })();
  }, [
    currentPage,
    searchStatus,
    totalCount,
    serverPageSize,
    highestServerPageLoaded,
    committedQuery,
    mode,
    committedFiltersKey,
    committedFilters
  ]);

  useEffect(() => {
    if (searchStatus !== "success") return;
    if (!totalCount) return;
    if (prefetchingRef.current) return;
    if (isFetchingMore) return;

    const controller = activeControllerRef.current;
    if (!controller) return;

    const runId = runIdRef.current;
    const query = committedQuery.trim();

    const schedule = (fn) => {
      if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(fn, { timeout: 1500 });
        return;
      }
      setTimeout(fn, 250);
    };

    schedule(async () => {
      if (prefetchingRef.current) return;
      prefetchingRef.current = true;

      try {
        const fn = mode === "full" ? searchProductsFull : searchProductsOpen;

        const maxServerPages = Math.ceil(totalCount / Math.max(1, serverPageSize));
        const wantUpTo = Math.min(
          maxServerPages,
          highestServerPageLoaded + SERVER_PREFETCH_PAGES_AHEAD,
          SERVER_PREFETCH_MAX_PAGES
        );

        for (let p = highestServerPageLoaded + 1; p <= wantUpTo; p += 1) {
          if (runIdRef.current !== runId) return;
          if (controller.signal.aborted) return;
          if (loadedServerPagesRef.current.has(p)) continue;
          if (inFlightPagesRef.current.has(p)) continue;

          inFlightPagesRef.current.add(p);
          try {
            const r = await fn(query, {
              signal: controller.signal,
              page: p,
              filters: committedFilters
            });

            if (runIdRef.current !== runId) return;

            const items = (r?.data || []).map(normalize2050ListItem);

            const uniqueItems = [];
            for (const it of items) {
              const key = getItemKey(it);
              if (!key) continue;
              if (seenItemKeysRef.current.has(key)) continue;
              seenItemKeysRef.current.add(key);
              uniqueItems.push(it);
            }

            loadedServerPagesRef.current.add(p);
            setBuffer((prev) => prev.concat(uniqueItems));
            setHighestServerPageLoaded((prev) => Math.max(prev, p));
            setDatasetInfo((prev) => prev || formatLoadStamp());
            await sleep(350);
          } catch (err) {
            if (isAbortError(err, controller)) return;
            console.error("Failed to prefetch 2050 Materials page", err);
            setLastServerError({
              status: err?.status ?? null,
              message: String(err?.message || err)
            });
          } finally {
            inFlightPagesRef.current.delete(p);
          }
        }
      } finally {
        prefetchingRef.current = false;
      }
    });
  }, [
    searchStatus,
    totalCount,
    serverPageSize,
    highestServerPageLoaded,
    committedQuery,
    mode,
    committedFiltersKey,
    committedFilters,
    isFetchingMore
  ]);

  const handleSelect = useCallback(
    async (item) => {
      if (!item?.uuid) return;
      setSelectedItem(item);
      setDetail(null);
      setDetailStatus("loading");
      setDetailError(null);

      const controller = new AbortController();

      try {
        const loader = mode === "full" ? loadProductDetailsFull : loadProductDetailsOpen;
        const raw = await loader(item.uuid, { signal: controller.signal });
        const normalized = normalize2050Detail(raw, { mode });

        setDetail(normalized);
        setDetailStatus("success");

        const impacts = Array.isArray(normalized?.impacts) ? normalized.impacts : [];
        setSelectedImpactId(impacts[0]?.id || null);
      } catch (e) {
        setDetailStatus("error");
        setDetailError(String(e?.message || e));
      }

      return () => controller.abort();
    },
    [mode]
  );

  const selectedImpact = useMemo(() => {
    const impacts = Array.isArray(detail?.impacts) ? detail.impacts : [];
    return impacts.find((i) => String(i.id) === String(selectedImpactId)) || impacts[0] || null;
  }, [detail, selectedImpactId]);

  const declaredUnitLabel = useMemo(() => {
    if (!detail?.declaredUnit?.unit) return null;
    return normalizeUnitLabel(`1 ${detail.declaredUnit.unit}`);
  }, [detail]);

  const isAlreadyShortlisted = useMemo(() => {
    if (!detail?.uuid) return false;
    return Array.isArray(shortlist) && shortlist.some((s) => String(s.uuid) === String(detail.uuid));
  }, [detail, shortlist]);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="space-y-0 pt-1 sm:pt-0">
          <div className="flex flex-col gap-2" style={{ marginBottom: "10px" }}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                <Input
                  ref={inputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitSearch();
                    }
                  }}
                  placeholder="Search products, or leave empty and use filters"
                  className="pl-9 pr-12 h-9 text-xs text-gray-900"
                />
                {searchStatus === "loading" && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 animate-spin" />
                )}
              </div>

              <Button
                type="button"
                className="h-9 px-3 bg-emerald-600 text-white hover:bg-emerald-700 w-full sm:w-auto"
                disabled={connectionStatus !== "success" || searchStatus === "loading"}
                onClick={commitSearch}
              >
                Search
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600" style={{ marginBottom: "16px" }}>
            {SAMPLE_QUERIES.map((sample) => (
              <Badge
                key={sample}
                onClick={() => handleSelectSample(sample)}
                className={`bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200 shadow-none py-1 px-2.5 ${String(sample || "").toLowerCase() === "gypsum" ? "hidden sm:inline-flex" : ""}`}
              >
                {sample}
              </Badge>
            ))}
          </div>

          <div className="text-xs text-gray-600 mt-2" style={{ marginBottom: "12px" }}>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700"
                    disabled={productTypeOptionsResponsive.length === 0}
                  >
                    <span>Product type</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">{productTypeSummary}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Product type</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {productTypeOptionsResponsive.length === 0 ? (
                    <DropdownMenuItem className="text-xs text-gray-500" disabled>
                      No product types
                    </DropdownMenuItem>
                  ) : (
                    productTypeOptionsResponsive.map((o) => (
                      <DropdownMenuCheckboxItem
                        key={o.id}
                        checked={selectedProductTypes.includes(o.id)}
                        className="text-xs"
                        onCheckedChange={(checked) => toggleValue(o.id, checked, setSelectedProductTypes)}
                      >
                        {o.name}
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-emerald-700"
                    onSelect={(e) => {
                      e.preventDefault();
                      setSelectedProductTypes([]);
                    }}
                  >
                    Clear selection
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700"
                    disabled={materialTypeOptionsResponsive.length === 0}
                  >
                    <span>Material type</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">{materialTypeSummary}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Material type</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {materialTypeOptionsResponsive.length === 0 ? (
                    <DropdownMenuItem className="text-xs text-gray-500" disabled>
                      No material types
                    </DropdownMenuItem>
                  ) : (
                    materialTypeOptionsResponsive.map((o) => (
                      <DropdownMenuCheckboxItem
                        key={o.id}
                        checked={selectedMaterialTypes.includes(o.id)}
                        className="text-xs"
                        onCheckedChange={(checked) => toggleValue(o.id, checked, setSelectedMaterialTypes)}
                      >
                        {o.name}
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-emerald-700"
                    onSelect={(e) => {
                      e.preventDefault();
                      setSelectedMaterialTypes([]);
                    }}
                  >
                    Clear selection
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700"
                    disabled={countryOptionsResponsive.length === 0}
                  >
                    <span>Country</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">{countrySummary}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Country</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {countryOptionsResponsive.length === 0 ? (
                    <DropdownMenuItem className="text-xs text-gray-500" disabled>
                      No countries
                    </DropdownMenuItem>
                  ) : (
                    countryOptionsResponsive.map((o) => (
                      <DropdownMenuCheckboxItem
                        key={o.id}
                        checked={selectedCountries.includes(o.id)}
                        className="text-xs"
                        onCheckedChange={(checked) => toggleValue(o.id, checked, setSelectedCountries)}
                      >
                        {o.name}
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-emerald-700"
                    onSelect={(e) => {
                      e.preventDefault();
                      setSelectedCountries([]);
                    }}
                  >
                    Clear selection
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[180px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700"
                    disabled={certificateTypeOptionsResponsive.length === 0}
                  >
                    <span>Certificate type</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">{certificateSummary}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Certificate type</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {certificateTypeOptionsResponsive.length === 0 ? (
                    <DropdownMenuItem className="text-xs text-gray-500" disabled>
                      No certificate types
                    </DropdownMenuItem>
                  ) : (
                    certificateTypeOptionsResponsive.map((o) => (
                      <DropdownMenuCheckboxItem
                        key={o.id}
                        checked={selectedCertificateTypes.includes(o.id)}
                        className="text-xs"
                        onCheckedChange={(checked) => toggleValue(o.id, checked, setSelectedCertificateTypes)}
                      >
                        {o.name}
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-emerald-700"
                    onSelect={(e) => {
                      e.preventDefault();
                      setSelectedCertificateTypes([]);
                    }}
                  >
                    Clear selection
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 px-3 text-xs text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => {
                  setSelectedProductTypes([]);
                  setSelectedMaterialTypes([]);
                  setSelectedCountries([]);
                  setSelectedCertificateTypes([]);
                }}
                disabled={
                  selectedProductTypes.length === 0 &&
                  selectedMaterialTypes.length === 0 &&
                  selectedCountries.length === 0 &&
                  selectedCertificateTypes.length === 0
                }
              >
                Clear all
              </Button>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-6 text-justify sm:text-left">
            Tip: Open data has limited data requset per hour, so limit your usage.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
        <Card className="bg-white border border-emerald-100 shadow self-start h-[650px] flex flex-col overflow-hidden">
          <CardHeader>
            <CardTitle className="text-gray-800">Matching products</CardTitle>
            <CardDescription>
              {searchStatus === "idle"
                ? "Enter at least two characters to search."
                : searchStatus === "loading"
                ? "Searching..."
                : totalCount > 0
                ? `Showing ${currentPage * UI_PAGE_SIZE + 1}-${Math.min(
                    currentPage * UI_PAGE_SIZE + pageResults.length,
                    totalCount
                  )} of ${totalCount}`
                : "No results"}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            {searchError && (
              <Alert variant="destructive" className="mb-4 flex-shrink-0">
                <AlertTitle>Search error</AlertTitle>
                <AlertDescription>{searchError}</AlertDescription>
              </Alert>
            )}
            {searchStatus === "idle" && (
              <div className="mb-3 text-sm text-gray-400 bg-emerald-50/30 border border-dashed border-emerald-200/50 rounded-xl p-4">
                Start typing a material name to see available datasets, or use the filters to search. Try "concrete", "timber", or "insulation".
              </div>
            )}

            <ScrollArea className="flex-1 min-h-0 mt-2 pr-2">
              <div className="space-y-3">
                {searchStatus === "loading" && buffer.length === 0
                  ? Array.from({ length: 10 }).map((_, idx) => <SkeletonProductItem key={`sk-${idx}`} />)
                  : pageResults.map((it) => (
                      <ProductResultItem
                        key={it.uuid || it.displayName}
                        item={it}
                        isSelected={String(selectedItem?.uuid) === String(it.uuid)}
                        onSelect={handleSelect}
                      />
                    ))}
              </div>
            </ScrollArea>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-100 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-800">
              <span className="font-medium text-emerald-900">
                {totalPages > 0 ? `Page ${currentPage + 1} of ${totalPages}` : "Page 1 of 1"}
              </span>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                    disabled={!canGoPrev}
                    className="h-7 w-7 text-emerald-700 hover:text-emerald-900 disabled:text-gray-300"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages - 1))}
                    disabled={!canGoNext}
                    className="h-7 w-7 text-emerald-700 hover:text-emerald-900 disabled:text-gray-300"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-emerald-100 self-start h-[650px] flex flex-col overflow-hidden">
            <CardHeader>
              <CardTitle className="text-gray-800 flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-emerald-600" />
                Product impact details
              </CardTitle>
            <CardDescription>Select a product to inspect declared unit and indicators.</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            <ScrollArea className="flex-1 min-h-0 pr-2">
              <div className="space-y-5">
                {detailStatus === "idle" && (
                  <div className="text-sm text-gray-400 bg-emerald-50/30 border border-emerald-200/50 rounded-xl p-4">
                    Choose a product on the left to load detailed 2050-materials impact metrics.
                  </div>
                )}

                {detailStatus === "loading" && (
                  <div className="flex items-center gap-3 text-emerald-700">
                    <Loader2 className="w-5 h-5 animate-spin" /> Fetching product details...
                  </div>
                )}

                {detailStatus === "error" && (
                  <Alert variant="destructive">
                    <AlertTitle>Could not load product</AlertTitle>
                    <AlertDescription>{detailError}</AlertDescription>
                  </Alert>
                )}

                {detailStatus === "success" && detail && (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 space-y-2">
                      <h3 className="text-lg font-semibold text-emerald-900">{detail.name}</h3>

                      <div className="text-sm text-gray-700 space-y-1">
                        <p>Manufacturer: {detail.manufacturer || "Not specified"}</p>
                        {detail.geography && <p>Location: {detail.geography}</p>}
                        {detail.created && <p>Created: {String(detail.created)}</p>}
                        {detail.updated && <p>Updated: {String(detail.updated)}</p>}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        {detail.declaredUnit?.unit && (
                          <Badge variant="outline" className="border-emerald-300 bg-white">
                            Declared unit: {declaredUnitLabel}
                          </Badge>
                        )}
                        {detail.product_url && (
                          <a href={detail.product_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline">
                            Open product page
                          </a>
                        )}
                        {detail.certificate_url && (
                          <a href={detail.certificate_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline">
                            Open certificate
                          </a>
                        )}
                      </div>

                      {onShortlist && detail.uuid && (
                        <div className="pt-2">
                          {!isAlreadyShortlisted ? (
                            <Button
                              type="button"
                              className="h-9 bg-emerald-600 text-white hover:bg-emerald-700"
                              onClick={() => {
                                const impacts = Array.isArray(detail.impacts) ? detail.impacts : [];
                                const chosen = impacts[0];
                                const modules = chosen?.modules || null;
                                const gwpTotal = Number(chosen?.total) || 0;

                                onShortlist({
                                  uuid: detail.uuid,
                                  name: detail.name,
                                  declaredUnit: detail.declaredUnit ? { unit: detail.declaredUnit.unit, amount: 1 } : null,
                                  gwpTotal,
                                  modules,
                                  materialProperties: null,
                                  geography: detail.geography || null,
                                  subtype: null,
                                  manufacturer: detail.manufacturer || null
                                });
                              }}
                            >
                              Shortlist
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              className="h-9 border-red-300 text-red-700 bg-white hover:bg-red-50"
                              onClick={() => onShortlist({ uuid: detail.uuid, __remove: true })}
                            >
                              Remove from shortlist
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <Separator className="bg-emerald-100" />
                      <h4 className="text-sm font-semibold text-emerald-700 uppercase tracking-wide">Impact categories</h4>

                      {Array.isArray(detail.impacts) && detail.impacts.length > 1 && (
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-600">Show impact</label>
                          <select
                            value={selectedImpactId || detail.impacts[0].id}
                            onChange={(e) => setSelectedImpactId(e.target.value)}
                            className="border border-emerald-200 rounded px-2 py-1 text-xs bg-white text-emerald-800"
                          >
                            {detail.impacts.map((imp) => (
                              <option key={imp.id} value={imp.id}>
                                {imp.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {selectedImpact && <ImpactCategoryTable impact={selectedImpact} declaredUnitLabel={declaredUnitLabel} />}
                    </div>

                    <details className="rounded-xl border border-emerald-100 bg-white p-3">
                      <summary className="cursor-pointer text-xs text-emerald-700 font-semibold">Debug raw payload</summary>
                      <pre className="mt-2 text-[11px] text-gray-700 whitespace-pre-wrap">{JSON.stringify(detail.raw, null, 2)}</pre>
                    </details>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Materials2050Explorer;
